import type { WasmFeatures } from "../../platform/platformCapabilities";
import { getPlatformCapabilities } from "../../platform/platformCapabilities";
import { getPlatformInfo } from "../../platform/platformInfo";

const wasmAssets = ["LensCoreWebAssembly.js", "LensCoreWebAssembly.wasm"];

export function getBuildFlavor({ simd, relaxedSimd }: WasmFeatures, versionNumber: string) {
    // Starting from approx build 282, LensCore now includes relaxed builds.
    // We need to check for this because we allow customization of Camera Kit
    // with any LensCore builds, which may not include a relaxed build.
    const relaxedBuildAvailable = parseInt(versionNumber) >= 282;
    if (relaxedBuildAvailable && relaxedSimd) {
        return "rel-relaxed-simd-neh";
    } else if (simd) {
        return "rel-simd-neh";
    } else {
        return "rel-neh";
    }
}

/**
 * Returns a list of URLs for resources which will be fetched during {@link bootstrapCameraKit}.
 *
 * When CameraKit is used on a website, these URLs must be reachable in order for CameraKit to be successfully
 * bootstrapped.
 *
 * @param endpointOverride Optional endpoint override to load the assets from.
 * @returns An array of asset URLs.
 *
 * @category Bootstrapping and Configuration
 */
export async function getRequiredBootstrapURLs(endpointOverride?: string): Promise<string[]> {
    // If we have an endpoint override, remove trailing `/` so we can construct a valid URL.
    const endpoint = endpointOverride?.replace(/[\/]+$/, "");

    const { wasm } = await getPlatformCapabilities();
    if (!wasm.supported) throw wasm.error;

    const { lensCore } = getPlatformInfo();
    const version = lensCore.version;
    const buildNumber = lensCore.buildNumber;
    const flavor = getBuildFlavor(wasm.wasmFeatures, version);
    return wasmAssets.map((asset) => {
        if (endpoint) return `${endpoint}/${asset}`;
        const { origin, pathname, search } = new URL(lensCore.baseUrl);
        return `${origin}${pathname}/${version}/${buildNumber}/${flavor}/${asset}${search}`;
    });
}
